import {
  BOOKING_SESSION_ID_HEADER,
  getOrCreateBookingSessionId
} from "../../../AnalyticsTracking/booking-session-id";
import {
  AvailableAppointmentTimesResponse,
  Breed,
  CareUnit,
  OnlineBooking,
  ProvetDepartment,
  ProvetFormData,
  Reason,
  ReasonGroup,
  ReasonGroupTuple,
  Species
} from "../types";
import { ApiClient, logApiError } from "./apiClient";
import { API_ENDPOINTS, API_TIMEOUTS } from "./provetConstants";

/**
 * Response from SMS verification creation endpoint
 */
export interface CreateVerificationResponse {
  requestId: string;
}

/**
 * Response from SMS verification check endpoint
 */
export interface CheckVerificationResponse {
  valid: boolean;
}

/**
 * Response from user existence check endpoint
 */
export interface CheckUserExistsResponse {
  exists: boolean;
}

/**
 * Interface for Provet API client methods.
 *
 * Every method reports a failure by rejecting (after logging it), so callers can tell
 * "the request failed" apart from "there is no data". An aborted request rejects with
 * an `AbortError`, which callers should ignore.
 */
export interface ProvetApiClient {
  /** Fetches reasons for a department. */
  fetchReasonsForDepartment: (id: number) => Promise<ReasonGroupTuple[]>;
  /** Fetches all departments. */
  fetchDepartments: () => Promise<ProvetDepartment[]>;

  /** Searches for departments by query and location. */
  searchForDepartment(
    query?: string,
    lat?: number,
    lng?: number
  ): Promise<ProvetDepartment[]>;

  /** Fetches available appointment times for a department and reason. */
  fetchAvailableAppointmentTimes: (
    startDate: Date,
    endDate: Date,
    departmentId: number,
    reasonId: number,
    duration: number,
    bufferTime: number,
    includeFirstAvailableDate: boolean,
    signal?: AbortSignal
  ) => Promise<AvailableAppointmentTimesResponse>;

  /** Fetches all species. */
  fetchSpecies(): Promise<Species[]>;

  /** Fetches all breeds. */
  fetchBreeds(): Promise<Breed[]>;

  /** Fetches all reason groups. */
  fetchReasonGroups(): Promise<ReasonGroup[]>;

  /** Fetches all reasons. */
  fetchReasons(): Promise<Reason[]>;

  /** Fetches a care unit by PMS ID. */
  fetchCareUnit(pmsId: string): Promise<CareUnit | null>;

  /** Submits a booking form. */
  submitBooking(formData: ProvetFormData): Promise<OnlineBooking>;

  /** Cancels a booking, with the cancel token from its booking response */
  cancelBooking(id: number, cancelToken: string): Promise<boolean>;

  /** Creates and sends a verification code via SMS */
  createVerification(
    phoneNumber: string,
    dispatchId?: string
  ): Promise<CreateVerificationResponse>;

  /** Checks if a verification code is valid */
  checkVerification(
    email: string,
    phoneNumber: string,
    code: string,
    sessionId: string,
    requestId: string
  ): Promise<CheckVerificationResponse>;

  /** Checks if a user exists in Provet by email and phone number */
  checkUserExists(
    email: string,
    phoneNumber: string,
    sessionId: string
  ): Promise<CheckUserExistsResponse>;
}

/**
 * ProvetApi provides methods for interacting with the Provet backend API.
 * Uses the generic ApiClient for consistent error handling and request management.
 */
export class ProvetApi implements ProvetApiClient {
  private readonly client: ApiClient;

  constructor(apiClient?: ApiClient, antiForgeryToken?: string) {
    const headers: Record<string, string> = {
      "Content-Type": "application/json"
    };

    // Add anti-forgery token header if provided (for CSRF protection)
    if (antiForgeryToken) {
      headers["X-CSRF-TOKEN"] = antiForgeryToken;
    }

    this.client =
      apiClient ||
      new ApiClient({
        baseUrl: "/api/provet",
        defaultHeaders: headers,
        // Evaluated per request: the id in sessionStorage can change (e.g. a new booking button click)
        dynamicHeaders: () => ({
          [BOOKING_SESSION_ID_HEADER]: getOrCreateBookingSessionId()
        }),
        defaultTimeout: API_TIMEOUTS.REQUEST
      });
  }

  /**
   * Fetches all departments.
   * @returns Promise resolving to an array of ProvetDepartment
   * @throws ApiError (or an AbortError) when the request fails
   */
  async fetchDepartments(): Promise<ProvetDepartment[]> {
    try {
      return await this.client.get<ProvetDepartment[]>(API_ENDPOINTS.DEPARTMENTS);
    } catch (error) {
      logApiError(error, "fetchDepartments");
      throw error;
    }
  }

  /**
   * Fetches reasons for a department.
   * @param id - Department ID
   * @returns Promise resolving to an array of ReasonGroupTuple
   * @throws ApiError (or an AbortError) when the request fails
   */
  async fetchReasonsForDepartment(id: number): Promise<ReasonGroupTuple[]> {
    try {
      return await this.client.get<ReasonGroupTuple[]>(
        API_ENDPOINTS.REASONS_FOR_DEPARTMENT(id)
      );
    } catch (error) {
      logApiError(error, `fetchReasonsForDepartment(${id})`);
      throw error;
    }
  }

  /**
   * Searches for departments by query and location.
   * @param query - Search query string
   * @param lat - Latitude
   * @param lng - Longitude
   * @returns Promise resolving to an array of ProvetDepartment
   * @throws ApiError (or an AbortError) when the request fails
   */
  async searchForDepartment(
    query?: string,
    lat?: number,
    lng?: number
  ): Promise<ProvetDepartment[]> {
    try {
      return await this.client.get<ProvetDepartment[]>(
        `${API_ENDPOINTS.DEPARTMENT_SEARCH}/${query}/${lat}/${lng}`
      );
    } catch (error) {
      logApiError(error, `searchForDepartment(${query}, ${lat}, ${lng})`);
      throw error;
    }
  }

  /**
   * Fetches available appointment times for a department and reason.
   * @param startDate - Start date
   * @param endDate - End date
   * @param departmentId - Department ID
   * @param reasonId - Reason ID
   * @param duration - Duration in minutes
   * @param bufferTime - Buffer time in minutes (default from constants)
   * @param signal - Optional abort signal
   * @returns Promise resolving to available times and the first available date after the selected month
   * @throws ApiError (or an AbortError) when the request fails
   */
  async fetchAvailableAppointmentTimes(
    startDate: Date,
    endDate: Date,
    departmentId: number,
    reasonId: number,
    duration: number,
    bufferTime: number = API_TIMEOUTS.APPOINTMENT_BUFFER,
    includeFirstAvailableDate: boolean = false,
    signal?: AbortSignal
  ): Promise<AvailableAppointmentTimesResponse> {
    try {
      return await this.client.get<AvailableAppointmentTimesResponse>(
        API_ENDPOINTS.APPOINTMENT_TIMES,
        {
          queryParams: {
            start: startDate.toISOString(),
            end: endDate.toISOString(),
            departmentId: departmentId,
            reasonId: reasonId,
            duration: duration,
            bufferTime: bufferTime,
            includeFirstAvailableDate: includeFirstAvailableDate
          },
          signal
        }
      );
    } catch (error) {
      logApiError(
        error,
        `fetchAvailableAppointmentTimes(dept:${departmentId}, reason:${reasonId})`
      );
      throw error;
    }
  }

  /**
   * Fetches all species.
   * @returns Promise resolving to an array of Species
   * @throws ApiError (or an AbortError) when the request fails
   */
  async fetchSpecies(): Promise<Species[]> {
    try {
      return await this.client.get<Species[]>(API_ENDPOINTS.SPECIES);
    } catch (error) {
      logApiError(error, "fetchSpecies");
      throw error;
    }
  }

  /**
   * Fetches all breeds.
   * @returns Promise resolving to an array of Breed
   * @throws ApiError (or an AbortError) when the request fails
   */
  async fetchBreeds(): Promise<Breed[]> {
    try {
      return await this.client.get<Breed[]>(API_ENDPOINTS.BREEDS);
    } catch (error) {
      logApiError(error, "fetchBreeds");
      throw error;
    }
  }

  /**
   * Fetches all reason groups.
   * @returns Promise resolving to an array of ReasonGroup
   * @throws ApiError (or an AbortError) when the request fails
   */
  async fetchReasonGroups(): Promise<ReasonGroup[]> {
    try {
      return await this.client.get<ReasonGroup[]>(API_ENDPOINTS.REASON_GROUPS);
    } catch (error) {
      logApiError(error, "fetchReasonGroups");
      throw error;
    }
  }

  /**
   * Fetches all reasons.
   * @returns Promise resolving to an array of Reason
   * @throws ApiError (or an AbortError) when the request fails
   */
  async fetchReasons(): Promise<Reason[]> {
    try {
      return await this.client.get<Reason[]>(API_ENDPOINTS.REASONS);
    } catch (error) {
      logApiError(error, "fetchReasons");
      throw error;
    }
  }

  /**
   * Fetches a care unit by PMS ID.
   * @param pmsId - PMS ID string
   * @returns Promise resolving to a CareUnit or null
   * @throws ApiError (or an AbortError) when the request fails
   */
  async fetchCareUnit(pmsId: string): Promise<CareUnit | null> {
    try {
      return await this.client.get<CareUnit>(API_ENDPOINTS.CARE_UNIT, {
        queryParams: { pmsId }
      });
    } catch (error) {
      logApiError(error, `fetchCareUnit(${pmsId})`);
      throw error;
    }
  }

  /**
   * Submits a booking form.
   * @param formData - The booking form data
   * @returns Promise resolving to an OnlineBooking
   */
  async submitBooking(formData: ProvetFormData): Promise<OnlineBooking> {
    // Simple validation
    if (!formData.selectedAppointmentTime?.availableTime) {
      throw new Error("No available time selected for booking.");
    }

    if (!formData.selectedDepartment) {
      throw new Error("No department selected for booking.");
    }

    if (!formData.selectedReason) {
      throw new Error("No reason selected for booking.");
    }

    if (!formData.contactDetails) {
      throw new Error("No contact details provided for booking.");
    }

    try {
      // Use ApiClient for consistent error handling and timeout management
      return await this.client.post<OnlineBooking>(API_ENDPOINTS.BOOKING, formData, {
        timeout: API_TIMEOUTS.BOOKING
      });
    } catch (error) {
      logApiError(error, "submitBooking");
      throw error;
    }
  }

  /**
   * Cancels a booking. The server only cancels it with the cancel token from the booking
   * response, so only the browser that made the booking can cancel it.
   * @param id - The booking ID to cancel
   * @param cancelToken - The cancel token from the booking response (a secret: never log it)
   * @returns Promise resolving to true if successful, false otherwise
   */
  async cancelBooking(id: number, cancelToken: string): Promise<boolean> {
    if (!id) {
      throw new Error("No booking id provided.");
    }

    try {
      // Use ApiClient for consistent error handling
      return await this.client.post<boolean>(API_ENDPOINTS.CANCEL_BOOKING(id), {
        cancelToken
      });
    } catch (error) {
      logApiError(error, `cancelBooking(${id})`);
      throw error;
    }
  }

  /**
   * Creates and sends a verification code via SMS
   * @param phoneNumber - The phone number to verify
   * @returns Promise resolving to response with requestId
   */
  async createVerification(
    phoneNumber: string,
    dispatchId?: string
  ): Promise<CreateVerificationResponse> {
    if (!phoneNumber) {
      throw new Error("Phone number is required");
    }

    try {
      return await this.client.post<CreateVerificationResponse>(
        API_ENDPOINTS.CREATE_VERIFICATION,
        { dispatchId, phoneNumber }
      );
    } catch (error) {
      logApiError(error, "createVerification");
      throw error;
    }
  }

  /**
   * Checks if a verification code is valid
   * @param email - The email address being verified
   * @param phoneNumber - The phone number being verified
   * @param code - The verification code to check
   * @param sessionId - The session ID for verification tracking
   * @param requestId - The request ID from verification creation
   * @returns Promise resolving to validation result
   */
  async checkVerification(
    email: string,
    phoneNumber: string,
    code: string,
    sessionId: string,
    requestId: string
  ): Promise<CheckVerificationResponse> {
    if (!email || !phoneNumber || !code || !sessionId || !requestId) {
      throw new Error(
        "Email, phone number, code, session ID, and request ID are required"
      );
    }

    try {
      return await this.client.post<CheckVerificationResponse>(
        API_ENDPOINTS.CHECK_VERIFICATION,
        {
          email,
          phoneNumber,
          code,
          sessionId,
          requestId
        }
      );
    } catch (error) {
      logApiError(error, "checkVerification");
      throw error;
    }
  }

  /**
   * Checks if a user already exists in Provet system
   * @param email - User email address
   * @param phoneNumber - User phone number
   * @param sessionId - The session ID for verification tracking
   * @returns Promise resolving to existence check result
   */
  async checkUserExists(
    email: string,
    phoneNumber: string,
    sessionId: string
  ): Promise<CheckUserExistsResponse> {
    if (!email || !phoneNumber || !sessionId) {
      throw new Error("Email, phone number, and session ID are required");
    }

    try {
      return await this.client.post<CheckUserExistsResponse>(
        API_ENDPOINTS.CHECK_USER_EXISTS,
        {
          email,
          phoneNumber,
          sessionId
        }
      );
    } catch (error) {
      logApiError(error, "checkUserExists");
      throw error;
    }
  }
}
